import React, { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import Portal from "../../../components/Portal";
import { AzureTeam } from "../../../types/azureTeams";
import useAzureService from "../../../services/azure";
import { HiX, HiOutlineUserGroup } from "react-icons/hi";
import CustomButton from "../../../components/common/CustomButton";
import useOrganizationService from "../../../services/organization";
import {useJwt} from "../../../helpers/common";
import {useResourcesService} from "../../../services";
import { removeImportedTeam } from "../../../redux/slices/azureTeams";

interface SyncTeamsModalProps {
  team: AzureTeam;
  onClose: () => void;
}

const SyncTeamsModal: React.FC<SyncTeamsModalProps> = ({ team, onClose }) => {
  const dispatch = useDispatch();
  const jwtData: any = useJwt();
  const { importAzureTeams, fetchAzureTeamsSilently, fetchAzureTeamSyncDifferences } = useAzureService();
  const { fetchTeams } = useOrganizationService();
  const { fetchManagerResources } = useResourcesService();
  const [importing, setImporting] = useState(false);

  const membersToImport = useMemo(() => 
    team.members
      .filter((m) => m.present_in_org === false)
      .slice()
      .sort((a, b) => a.display_name.localeCompare(b.display_name)), 
    [team]
  );

  const allMembersImported = membersToImport.length === 0 && team.members.length > 0;

  const handleImport = async () => {
    setImporting(true);
    try {
      const teamPayload: AzureTeam = {
        ...team,
        selected: true,
        // Send all members (both new and already imported) so backend can map them to the team
        members: team.members.map(m => ({ ...m, selected: true }))
      } as any;
      await importAzureTeams([teamPayload]);

      // Close modal immediately after successful import
      onClose();

      // Remove the team from Redux state (no API call needed)
      dispatch(removeImportedTeam(team.team_id));

      // Refresh other data in background
      const refreshPromises: Promise<any>[] = [];
      
      if(jwtData.org_id) {
        refreshPromises.push(fetchTeams(jwtData.org_id));
        refreshPromises.push(fetchManagerResources(jwtData.org_id));
        
        if(jwtData.user_id) {
          refreshPromises.push(fetchAzureTeamSyncDifferences(jwtData.org_id, jwtData.user_id));
        }
      }
      
      // Wait for all refresh calls to complete in background
      await Promise.all(refreshPromises);
    } catch (error) {
      console.error(error);
      setImporting(false);
    }
  };

  return (
    <Portal>
      <div className="fixed inset-0 z-[9999] flex items-center justify-center px-4 py-6">
        <button
          type="button"
          aria-label="Close import team modal"
          className="absolute inset-0 w-full h-full bg-black/50 backdrop-blur-sm cursor-default z-10 disabled:cursor-not-allowed"
          onClick={onClose}
          disabled={importing}
        />
        <dialog
          open
          className="z-20 bg-secondary-light dark:bg-secondary-dark rounded-lg w-full max-w-xl mx-auto self-center shadow-2xl transform transition-all max-h-[85vh] flex flex-col"
          aria-labelledby="import-team-title"
        >
          <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                  <HiOutlineUserGroup className="w-6 h-6 text-blue-500 dark:text-blue-400" />
                </div>
                <h3 id="import-team-title" className="text-lg font-semibold text-gray-900 dark:text-white">
                  Confirm Team Import
                </h3>
              </div>
              <button
                type="button"
                onClick={onClose}
                disabled={importing}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="sr-only">Close</span>
                <HiX className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div className="px-6 py-4 flex-shrink-0">
            <p className="text-sm text-gray-700 dark:text-gray-300">
              {allMembersImported ? (
                <>
                  You're about to import the team
                  <span className="font-semibold"> "{team.team_name}"</span>.
                  All {team.members.length} people are already in Cadence Pro. This will import the team and map them to it.
                </>
              ) : (
                <>
                  You're about to import the team
                  <span className="font-semibold"> "{team.team_name}"</span>.
                  This will add {membersToImport.length} people to Cadence Pro and create the team with member mapping.
                </>
              )}
            </p>
          </div>

          <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
            <div className="flex space-x-3 justify-end items-center">
              <CustomButton
                variant="secondary"
                type="button"
                onClick={onClose}
                disabled={importing}
              >
                Cancel
              </CustomButton>
              <CustomButton
                onClick={handleImport}
                disabled={importing}
              >
                {importing ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                    </svg>
                    Importing...
                  </>
                ) : (
                  <>
                    Import
                  </>
                )}
              </CustomButton>
            </div>
          </div>
        </dialog>
      </div>
    </Portal>
  );
};

export default SyncTeamsModal;